import type { NodeLabel } from '@wt-codegraph/shared';

import type { GraphDto, GraphNodeDto } from '../api/graph.js';

import { SYMBOL_KINDS } from './graph-node-attributes.js';

export const STRUCTURAL_KINDS = new Set<NodeLabel>(['Repo', 'Module', 'Namespace', 'Directory']);
export const HIERARCHY_RELATIONS = new Set(['CONTAINS', 'DEFINES', 'IMPORTS']);

export interface SeedPosition {
  readonly x: number;
  readonly y: number;
}

export interface ParentChildIndex {
  readonly childToParent: Map<string, string>;
  readonly parentToChildren: Map<string, string[]>;
}

export function buildParentChildIndex(dto: GraphDto): ParentChildIndex {
  const childToParent = new Map<string, string>();
  const parentToChildren = new Map<string, string[]>();
  for (const edge of dto.edges) {
    if (!HIERARCHY_RELATIONS.has(edge.type)) continue;
    childToParent.set(edge.target, edge.source);
    let kids = parentToChildren.get(edge.source);
    if (!kids) {
      kids = [];
      parentToChildren.set(edge.source, kids);
    }
    kids.push(edge.target);
  }
  return { childToParent, parentToChildren };
}

export interface ClusterCenters {
  readonly map: ReadonlyMap<number, SeedPosition>;
  readonly jitter: number;
}

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

export function buildClusterCenters(
  communityMemberships: ReadonlyMap<string, number> | undefined,
  structuralSpread: number,
  nodeCount: number,
): ClusterCenters {
  const map = new Map<number, SeedPosition>();
  if (!communityMemberships || communityMemberships.size === 0) {
    return { map, jitter: 0 };
  }
  const communities = new Set(communityMemberships.values());
  const clusterSpread = structuralSpread * 0.8;
  let idx = 0;
  for (const c of communities) {
    const angle = idx * GOLDEN_ANGLE;
    const radius = clusterSpread * Math.sqrt((idx + 1) / communities.size);
    map.set(c, { x: radius * Math.cos(angle), y: radius * Math.sin(angle) });
    idx++;
  }
  return { map, jitter: Math.sqrt(Math.max(nodeCount, 1)) * 1.5 };
}

export function seedStructuralPositions(
  structural: readonly GraphNodeDto[],
  structuralSpread: number,
): Map<string, SeedPosition> {
  const positions = new Map<string, SeedPosition>();
  structural.forEach((node, index) => {
    const angle = index * GOLDEN_ANGLE;
    const radius = structuralSpread * Math.sqrt((index + 1) / Math.max(structural.length, 1));
    const jitter = structuralSpread * 0.15;
    const x = radius * Math.cos(angle) + (Math.random() - 0.5) * jitter;
    const y = radius * Math.sin(angle) + (Math.random() - 0.5) * jitter;
    positions.set(node.id, { x, y });
  });
  return positions;
}

export interface ChildSeedArgs {
  readonly child: GraphNodeDto;
  readonly parentPos?: SeedPosition;
  readonly clusters: ClusterCenters;
  readonly community?: number;
  readonly childJitter: number;
}

export function seedChildPosition(args: ChildSeedArgs): SeedPosition {
  const center =
    args.community !== undefined && SYMBOL_KINDS.has(args.child.kind)
      ? args.clusters.map.get(args.community)
      : undefined;
  const base = center ?? args.parentPos ?? { x: 0, y: 0 };
  const jitter = center ? args.clusters.jitter : args.childJitter;
  return {
    x: base.x + (Math.random() - 0.5) * jitter,
    y: base.y + (Math.random() - 0.5) * jitter,
  };
}

export function randomFallbackPosition(structuralSpread: number): SeedPosition {
  return {
    x: (Math.random() - 0.5) * structuralSpread * 0.5,
    y: (Math.random() - 0.5) * structuralSpread * 0.5,
  };
}
